import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, CheckCircle2, Clock, Plus, Trash2, Edit2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import ScheduleActivityDialog from "../components/ScheduleActivityDialog";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

const TYPE_COLORS = {
  call: "bg-blue-50 text-blue-700 border-blue-200",
  meeting: "bg-violet-50 text-violet-700 border-violet-200",
  email: "bg-orange-50 text-orange-700 border-orange-200",
  follow_up: "bg-amber-50 text-amber-700 border-amber-200",
  demo: "bg-emerald-50 text-emerald-700 border-emerald-200",
  task: "bg-slate-100 text-slate-700 border-slate-200",
};

const TYPE_LABEL = {
  call: "Call", meeting: "Meeting", email: "Email",
  follow_up: "Follow-up", demo: "Demo", task: "Task",
};

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function shiftDate(iso, delta) {
  const d = new Date(iso);
  d.setDate(d.getDate() + delta);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function fmtTime(s) {
  if (!s) return "—";
  const d = new Date(s);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function MyDay() {
  const { user } = useAuth();
  const isManager = user && ["super_admin", "admin", "sales_manager"].includes(user.role);
  const [date, setDate] = useState(todayIso());
  const [assigneeId, setAssigneeId] = useState("");
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const aid = assigneeId || (isManager ? "" : user?.id);
      const qp = new URLSearchParams();
      qp.set("date", date);
      if (aid) qp.set("assignee_id", aid);
      const [list, summ] = await Promise.all([
        api.get(`/scheduled-activities?${qp.toString()}`),
        api.get(`/scheduled-activities/summary?${qp.toString()}`),
      ]);
      setItems(list.data);
      setSummary(summ.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    }
  };

  useEffect(() => { load();    }, [date, assigneeId]);

  useEffect(() => {
    (async () => {
      if (isManager) {
        const { data } = await api.get("/users");
        setUsers(data.filter((u) => u.active !== false));
      }
    })();
  }, [isManager]);

  const toggleComplete = async (a) => {
    try {
      const next = a.status === "completed" ? "planned" : "completed";
      await api.patch(`/scheduled-activities/${a.id}`, { status: next });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const remove = async (a) => {
    try {
      await api.delete(`/scheduled-activities/${a.id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const totals = summary?.totals || { planned: 0, completed: 0, cancelled: 0, total: 0, completion_rate: 0 };
  const grouped = useMemo(() => {
    const g = {};
    items.forEach((a) => {
      const key = a.assignee_name || "Unassigned";
      g[key] = g[key] || [];
      g[key].push(a);
    });
    return g;
  }, [items]);

  return (
    <Layout title="My Day" breadcrumb={["Activities", "My Day"]}>
      {/* Header controls */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5 shadow-sm flex flex-wrap items-center gap-3">
        <button data-testid="day-prev" onClick={() => setDate(shiftDate(date, -1))} className="p-2 rounded-md hover:bg-slate-50 border border-slate-200"><ChevronLeft size={14} /></button>
        <input data-testid="day-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="px-3 py-1.5 text-sm border border-slate-200 rounded-md" />
        <button data-testid="day-next" onClick={() => setDate(shiftDate(date, +1))} className="p-2 rounded-md hover:bg-slate-50 border border-slate-200"><ChevronRight size={14} /></button>
        <button data-testid="day-today" onClick={() => setDate(todayIso())} className="text-sm font-medium px-3 py-1.5 rounded-md border border-slate-200 bg-white">Today</button>

        {isManager && (
          <select
            data-testid="day-assignee-filter"
            value={assigneeId}
            onChange={(e) => setAssigneeId(e.target.value)}
            className="px-3 py-1.5 text-sm border border-slate-200 rounded-md ml-2"
          >
            <option value="">Whole team</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        )}

        <div className="flex-1" />

        <button
          data-testid="day-schedule-btn"
          onClick={() => { setEditing(null); setOpenDialog(true); }}
          className="text-sm font-medium px-3 py-1.5 rounded-md bg-orange-500 hover:bg-orange-600 text-white flex items-center gap-1.5"
        >
          <Plus size={14} /> Schedule activity
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <Stat label="Total planned" value={totals.total} accent="bg-slate-50 text-slate-700" testid="kpi-day-total" />
        <Stat label="Planned (open)" value={totals.planned} accent="bg-orange-50 text-orange-700" testid="kpi-day-planned" />
        <Stat label="Completed" value={totals.completed} accent="bg-emerald-50 text-emerald-700" testid="kpi-day-completed" />
        <Stat label="Completion rate" value={`${totals.completion_rate}%`} accent="bg-blue-50 text-blue-700" testid="kpi-day-rate" />
      </div>

      {/* Manager: team breakdown */}
      {isManager && !assigneeId && summary?.by_assignee?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm mb-5">
          <h3 className="text-base font-semibold text-slate-900 mb-4">Team scorecard — {date}</h3>
          <table className="w-full text-sm">
            <thead>
              <tr>{["Assignee", "Planned", "Completed", "Cancelled", "Total", "Completion %"].map((h) => (
                <th key={h} className="text-left py-2 text-xs uppercase tracking-wider text-slate-500">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {summary.by_assignee.map((r) => (
                <tr key={r.assignee_id} className="border-t border-slate-100">
                  <td className="py-2 font-medium">{r.assignee}</td>
                  <td className="py-2 text-orange-600">{r.planned}</td>
                  <td className="py-2 text-emerald-600">{r.completed}</td>
                  <td className="py-2 text-slate-500">{r.cancelled}</td>
                  <td className="py-2">{r.total}</td>
                  <td className="py-2 font-semibold text-blue-600">{r.completion_rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Activity list */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2"><CalendarDays size={16} className="text-orange-500" /> Activities for {date}</h3>
          <span className="text-xs text-slate-500">{items.length} activities</span>
        </div>
        {items.length === 0 && (
          <div className="px-5 py-12 text-center">
            <CalendarDays size={36} className="mx-auto text-slate-300 mb-2" />
            <div className="text-sm text-slate-500">No activities scheduled. Click "Schedule activity" to add one.</div>
          </div>
        )}
        <ul>
          {Object.entries(grouped).map(([owner, list]) => (
            <li key={owner}>
              {(isManager && !assigneeId) && (
                <div className="px-5 py-2 bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500 border-y border-slate-100">{owner}</div>
              )}
              {list.map((a) => (
                <div key={a.id} data-testid={`activity-row-${a.id}`} className="px-5 py-3 border-b border-slate-100 flex items-center gap-3 hover:bg-orange-50/30">
                  <button
                    data-testid={`activity-toggle-${a.id}`}
                    onClick={() => toggleComplete(a)}
                    className={`shrink-0 w-6 h-6 rounded-full border-2 grid place-items-center transition-colors ${
                      a.status === "completed" ? "bg-emerald-500 border-emerald-500" : "border-slate-300 hover:border-emerald-400"
                    }`}
                    title={a.status === "completed" ? "Mark as planned" : "Mark complete"}
                  >
                    {a.status === "completed" && <CheckCircle2 size={14} className="text-white" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${TYPE_COLORS[a.type] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                        {TYPE_LABEL[a.type] || a.type}
                      </span>
                      <Link to={`/leads/${a.lead_id}`} className="text-sm font-medium text-slate-900 hover:text-orange-600 truncate">
                        {a.lead_company}
                      </Link>
                      {a.status === "completed" && (
                        <span className="text-[10px] text-emerald-600 font-medium uppercase tracking-wider">Done</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                      <Clock size={11} /> {fmtTime(a.scheduled_at)}
                      <span>•</span>
                      <span>{a.assignee_name}</span>
                      {a.notes && <><span>•</span><span className="truncate">{a.notes}</span></>}
                    </div>
                  </div>
                  <button
                    data-testid={`activity-edit-${a.id}`}
                    onClick={() => { setEditing(a); setOpenDialog(true); }}
                    className="text-slate-400 hover:text-slate-700 p-1.5 rounded-md hover:bg-slate-100"
                    title="Edit"
                  >
                    <Edit2 size={14} />
                  </button>
                  <button
                    data-testid={`activity-delete-${a.id}`}
                    onClick={() => remove(a)}
                    className="text-slate-400 hover:text-red-600 p-1.5 rounded-md hover:bg-red-50"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </li>
          ))}
        </ul>
      </div>

      <ScheduleActivityDialog
        open={openDialog}
        onClose={() => { setOpenDialog(false); setEditing(null); }}
        onSaved={() => { setOpenDialog(false); setEditing(null); load(); }}
        initial={editing}
        canAssignToOthers={isManager}
      />
    </Layout>
  );
}

function Stat({ label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <div className={`inline-block text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${accent}`}>{label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-2">{value}</div>
    </div>
  );
}
